import React from 'react';
import {
  Button,
  CondensedLarge,
  DateUtil,
  Flashy,
  Form,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalSection,
  Textarea,
} from '@axiom/ui';
import type { CandidateReputationDetails } from '@axiom/validation';
import { CandidateReputationActions } from '@axiom/const';

import type { ReputationFeedback } from '../../schema/reputation-feedback-form-schema';
import { ReputationFeedbackFormSchema } from '../../schema/reputation-feedback-form-schema';
import { ActionReputationApi } from '../../api/action-reputation-api';

import { ReputationDetailsUtil } from './ReputationDetailsUtil';

const DEFAULT_FEEDBACK_NOTE =
  "I'm sending the below feedback for your records. Feel free to reach out to me if you want to discuss this feedback live.";

const formatScore = (score: number | null | undefined): string =>
  (score ?? -1) < 0 ? 'N/A' : String(score);

export type ReputationDetailsFeedbackModalProps = {
  onClose: () => void;
  onSubmit: () => Promise<void>;
  feedBackData: CandidateReputationDetails;
};

export const ReputationDetailsFeedbackModal = ({
  onClose,
  onSubmit,
  feedBackData,
}: ReputationDetailsFeedbackModalProps) => {
  const handleOnSubmit = async (
    _changedData: Partial<ReputationFeedback>,
    _actions: unknown,
    values: ReputationFeedback
  ) => {
    await ActionReputationApi.createActionReputationDetails({
      reputationId: feedBackData.id,
      body: {
        action: CandidateReputationActions.SHARED,
        actionNotes: values.feedbackText || null,
      },
    });

    await onSubmit();
  };

  const formattedDate = DateUtil.displayLongMonthDayYear(
    feedBackData.responseDate
  );

  return (
    <Form
      name="FEEDBACK_FORM"
      initialValues={{ feedbackText: DEFAULT_FEEDBACK_NOTE }}
      schema={ReputationFeedbackFormSchema}
      onSubmit={handleOnSubmit}
    >
      {({ fireSubmit }) => {
        return (
          <Modal name="WRITTEN_FEEDBACK_MODAL">
            <ModalHeader
              name="FEEDBACK_MODAL_HEADER"
              onClose={() => {
                onClose();
              }}
            >
              Send feedback to talent
            </ModalHeader>
            <ModalSection>
              <CondensedLarge name="DESCRIPTION_TEXT">
                {feedBackData.candidate.calculatedDisplayName} will receive this
                feedback by email. The talent owner will be CC'd. Here's what's
                included:
              </CondensedLarge>
              <CondensedLarge>
                <ul data-test="FEEDBACK_PREVIEW_LIST">
                  <li data-test="SURVEY_SUMMARY">
                    <Flashy bold>
                      {feedBackData.respondentName ?? 'Unverified Respondent'}
                    </Flashy>{' '}
                    from{' '}
                    <Flashy bold>
                      {feedBackData.opportunity.account?.name}
                    </Flashy>{' '}
                    submitted a new performance feedback survey on{' '}
                    <Flashy bold>{formattedDate ?? 'Unverified Date'}</Flashy>{' '}
                    evaluating your performance on{' '}
                    <Flashy bold>
                      {feedBackData.opportunity.jobName ??
                        'Unverified Engagement'}
                    </Flashy>
                    .
                  </li>
                  <li data-test="RECOMMEND_SCORE">
                    <Flashy bold>Likelihood to recommend (0-10)</Flashy>{' '}
                    {formatScore(feedBackData.clientRecommendScore)}
                  </li>
                  <li data-test="CATEGORY_SCORES">
                    <Flashy bold>Legal Acumen</Flashy>{' '}
                    {ReputationDetailsUtil.formatScores(
                      feedBackData.legalExpertiseScore
                    )}
                    ; <Flashy bold>Business Judgment</Flashy>{' '}
                    {ReputationDetailsUtil.formatScores(
                      feedBackData.businessJudgementScore
                    )}
                    ; <Flashy bold>Collaboration</Flashy>{' '}
                    {ReputationDetailsUtil.formatScores(
                      feedBackData.communicationScore
                    )}
                    ; <Flashy bold>Responsiveness</Flashy>{' '}
                    {ReputationDetailsUtil.formatScores(
                      feedBackData.responsivenessScore
                    )}
                    ; with context that 3 is "Meets Expectations";
                  </li>
                  {feedBackData.retiredDidWellOnImproveOn ? (
                    <li data-test="RETIRED_FEEDBACK">
                      {feedBackData.retiredDidWellOnImproveOn}
                    </li>
                  ) : (
                    <>
                      <li data-test="DID_WELL_ON">
                        <Flashy bold>What did talent do well?</Flashy>{' '}
                        {feedBackData.didWellOn || 'N/A'}
                      </li>
                      <li data-test="IMPROVE_ON">
                        <Flashy bold>What can talent do to improve?</Flashy>{' '}
                        {feedBackData.improveOn || 'N/A'}
                      </li>
                    </>
                  )}
                  <li data-test="REMINDER_NOTE">
                    A reminder to reach out to us, not the client, if they'd
                    like to discuss further.
                  </li>
                </ul>
              </CondensedLarge>
              <Textarea
                formatting="modern"
                name="feedbackText"
                label="Add a personalized note"
                full
              />
            </ModalSection>
            <ModalFooter>
              <Button
                pattern="primary"
                variation="outline"
                name="CANCEL_BUTTON"
                onClick={() => {
                  onClose();
                }}
              >
                Cancel
              </Button>
              <Button
                pattern="primary"
                name="SEND_FEEDBACK_BUTTON"
                onClick={async () => {
                  await fireSubmit();
                }}
              >
                Send Feedback
              </Button>
            </ModalFooter>
          </Modal>
        );
      }}
    </Form>
  );
};
